import { getStoreAvailability } from '@theme/store-availability';

/**
 * @typedef {import('@theme/events').VariantUpdateEvent} VariantUpdateEvent
 */

/**
 * @param {string} str
 * @returns {string}
 */
function escapeHtml(str) {
  const node = document.createElement('div');
  node.appendChild(document.createTextNode(String(str)));
  return node.innerHTML;
}

/**
 * @param {HTMLElement} container
 */
function init(container) {
  const list = container.querySelector('[data-locations-list]');
  const shopDomain = container.dataset.shopDomain;
  const storefrontToken = container.dataset.storefrontToken;

  if (!list) return;

  if (!shopDomain || !storefrontToken) {
    list.innerHTML = '<li class="product-inventory-locations__error">Storefront-Token nicht konfiguriert.</li>';
    return;
  }

  function setLoading() {
    if (!list) return;
    list.setAttribute('aria-busy', 'true');
    list.innerHTML = '<li class="product-inventory-locations__loading" role="status">…</li>';
  }

  /**
   * @param {Array<{id: string, name: string, stock: number}>} locations
   */
  function renderLocations(locations) {
    if (!list) return;
    list.removeAttribute('aria-busy');
    if (!locations.length) {
      list.innerHTML = '<li class="product-inventory-locations__empty">Keine Filial-Daten verfügbar.</li>';
      return;
    }
    list.innerHTML = locations
      .map(({ name, stock }) => {
        const available = stock > 0;
        const stateClass = available
          ? 'product-inventory-locations__row--available'
          : 'product-inventory-locations__row--unavailable';
        const label = available ? 'Verfügbar' : 'Nicht verfügbar';
        const stockHint = available
          ? `<span class="product-inventory-locations__stock">(${stock}&nbsp;Stk.)</span>`
          : '';
        return `<li class="product-inventory-locations__row ${stateClass}">
          <span class="product-inventory-locations__name">${escapeHtml(name)}</span>
          <span class="product-inventory-locations__status">
            <span class="product-inventory-locations__dot" aria-hidden="true"></span>
            ${label} ${stockHint}
          </span>
        </li>`;
      })
      .join('');
  }

  function renderError() {
    if (!list) return;
    list.removeAttribute('aria-busy');
    list.innerHTML = '<li class="product-inventory-locations__error">Verfügbarkeit konnte nicht geladen werden.</li>';
  }

  /**
   * @param {string} [variantId]
   */
  async function fetchAvailability(variantId) {
    if (!variantId || !shopDomain || !storefrontToken) return;
    setLoading();

    try {
      const locations = await getStoreAvailability(variantId, { shopDomain, storefrontToken });
      renderLocations(locations);
    } catch {
      renderError();
    }
  }

  // Initial load
  fetchAvailability(container.dataset.variantId);

  // Matches product-sku.js's ancestor selector (confirmed reliable for this shop's
  // markup) rather than the more common `.shopify-section, dialog` used elsewhere,
  // which doesn't reliably deliver the variant:update event here.
  const target = container.closest('[id*="ProductInformation-"], [id*="QuickAdd-"], product-card');
  target?.addEventListener('variant:update', (event) => {
    // Note: VariantUpdateEvent extends the plain Event class (not CustomEvent), even
    // though it carries a `.detail` payload - don't guard on `instanceof CustomEvent`.
    const id = /** @type {VariantUpdateEvent} */ (event).detail?.resource?.id;
    if (!id) return;
    container.dataset.variantId = id;
    fetchAvailability(id);
  });
}

document.querySelectorAll('[data-inventory-locations]').forEach((container) => {
  if (container instanceof HTMLElement) init(container);
});
